import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import { useMaintenanceWindow } from "@shared/hooks/useMaintenanceStatus";

interface MonitoringUnavailableProps {
  onRetry?: () => void;
}

/**
 * Shown when the overview could not load its services, instead of the empty
 * state.
 *
 * `EmptyItems` says "your tenant is currently empty", which is a statement
 * about the customer's inventory. When the request simply failed we know
 * nothing about their inventory, and saying that to someone with dozens of
 * provisioned services is both alarming and wrong. The copy leads with what
 * is true and reassuring — the services are running, we just cannot see them.
 */
const MonitoringUnavailable: React.FC<MonitoringUnavailableProps> = ({
  onRetry,
}) => {
  const maintenance = useMaintenanceWindow("Kontrol Edge");

  const until = maintenance ? new Date(maintenance.until) : null;
  const untilLabel =
    until && !Number.isNaN(until.getTime())
      ? until.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : null;

  return (
    <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center">
      <div className="w-1/2">
        <GenericCard
          isCollapsible={false}
          bodyContent={
            <div className="flex flex-col items-center justify-center text-center space-y-4 p-6">
              <div className="bg-semantic-bg-warning-secondary text-semantic-text-warning inline-flex items-center justify-center border border-[var(--color-utility-warning-600)] rounded-md p-2">
                <svg
                  className="h-10 w-10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </div>

              <h3 className="text-1xl font-bold">
                {maintenance
                  ? "Your services are running — monitoring is paused"
                  : "We couldn't load your services right now"}
              </h3>

              <p className="text-sm text-semantic-text-secondary">
                {maintenance
                  ? "We are working on the monitoring platform, so we cannot show live status. Traffic, routing and configuration are unaffected and nothing has changed on your side."
                  : "Live status is temporarily unavailable. This does not mean your services are down — your traffic and configuration are unaffected."}
              </p>

              {untilLabel && (
                <p className="text-xs text-semantic-text-secondary">
                  Maintenance window ends at {untilLabel}.
                </p>
              )}

              {onRetry && (
                <GenericButton
                  label="Retry"
                  variant="brand"
                  size="md"
                  onClick={onRetry}
                />
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MonitoringUnavailable;
